import { useMemo, useState } from 'react';

import { useEntertainerEngagementContext } from '@contexts/EntertainerEngagementContext';

import {
  UnauthorizedPublicEntertainer,
  useCurrentEntertainerQuery,
  useEntertainerByBookingHandleQuery,
} from '@queries/EntertainerQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { ImageGrid } from '@components/layout/ImageGrid/ImageGrid';

import { FeaturedLookPopup } from '../FeaturedLooksScreen/FeaturedLooksScreen';

interface EntertainerFeaturedMediaImage {
  url: string;
  featuredMedia: UnauthorizedPublicEntertainer['featuredMedia'][number];
}

const EntertainerMediaTabContentComponent = ({ bookingHandle }: { bookingHandle: string }) => {
  const { track } = useAnalytics();
  const { entertainer } = useEntertainerByBookingHandleQuery(bookingHandle);
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const [selectedImage, setSelectedImage] = useState<EntertainerFeaturedMediaImage>();
  const { openAddLookPopup, openAddVideoPopup } = useEntertainerEngagementContext();

  const isCurrentEntertainer = currentEntertainer?._id === entertainer?._id;

  const images = useMemo<EntertainerFeaturedMediaImage[]>(
    () =>
      entertainer?.featuredMedia?.map((featuredMedia) => ({
        url: featuredMedia.image,
        featuredMedia,
      })) ?? [],
    [entertainer],
  );

  const handleAddImage = () => {
    openAddLookPopup({
      cityId: entertainer?.cities?.[0]?._id,
    });
  };

  const handleAddVideo = () => {
    openAddVideoPopup({
      cityId: entertainer?.cities?.[0]?._id,
    });
  };

  const handleClick = (image: EntertainerFeaturedMediaImage) => {
    track('Click Entertainer Media', { image });
    setSelectedImage(image);
  };

  return (
    <>
      <div>
        {images?.length ? (
          <>
            {isCurrentEntertainer && (
              <div className="d-flex gap-2 mb-2">
                <Button
                  startIcon={<FontAwesomeIcon name="image" />}
                  variant="outlined"
                  color="neutral"
                  roundness="rounded"
                  onClick={handleAddImage}
                >
                  Add image
                </Button>
                <Button
                  startIcon={<FontAwesomeIcon name="video" />}
                  variant="outlined"
                  color="neutral"
                  roundness="rounded"
                  onClick={handleAddVideo}
                >
                  Add video
                </Button>
              </div>
            )}
            <ImageGrid images={images} onClick={handleClick} />
          </>
        ) : (
          <>
            {isCurrentEntertainer ? (
              <>
                <div>
                  <EmptyState
                    title="No featured looks"
                    text="You have not added any featured looks yet. Add a photo to start showing off your best looks!"
                    button={{
                      text: 'Add a Featured Look',
                      onClick: handleAddImage,
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <EmptyState
                  title="No featured looks"
                  text="This entertainer has not added any featured looks yet."
                />
              </>
            )}
          </>
        )}
      </div>
      {entertainer && (
        <FeaturedLookPopup
          isVisible={!!selectedImage}
          onClose={() => setSelectedImage(undefined)}
          entertainerMedia={selectedImage?.featuredMedia}
          entertainer={entertainer}
        />
      )}
    </>
  );
};

export const EntertainerMediaTabContent = ({ bookingHandle }: { bookingHandle?: string }) => {
  return bookingHandle ? (
    <EntertainerMediaTabContentComponent bookingHandle={bookingHandle} />
  ) : null;
};
